const NECESSARY = 'necessary';
const FUNCTIONAL = 'functional';
const STATISTICS = 'statistics';
const MARKETING = 'marketing';

const oneTrustCategoryCodeToCategoryName: Record<string, string> = {
  C0001: NECESSARY,
  C0002: FUNCTIONAL,
  C0003: STATISTICS,
  C0004: MARKETING,
};

const COOKIE_CHECK_INTERVAL_MS = 3000;
const ONETRUST_CONSENT_COOKIE_NAME = 'OptanonConsent';

const getGroupsParamFromCookieValue = (
  cookieValue: string,
): string | undefined => {
  return cookieValue.split('&').find((param) => param.startsWith('groups='));
};

const parseGroupsParamToCategoryAcceptanceList = (
  groupsParamString: string,
): string[] => {
  const splitParams = groupsParamString.split('=');
  const value = splitParams[1] || '';

  return decodeURIComponent(value).split(',');
};

const parseInteractionCountParamToNumber = (
  interactionParamString: string,
): number => {
  const value = interactionParamString.split('=')[1];
  return value ? +value : 0;
};

const splitCategoryCodeAndAcceptance = (
  commaSeparatedCatAndAcceptance: string,
): [string, string] =>
  commaSeparatedCatAndAcceptance.split(':').map(decodeURIComponent) as [
    string,
    string,
  ];

const getInteractionCountParamFromCookieValue = (
  cookieValue: string,
): string | undefined => {
  return cookieValue
    .split('&')
    .find((param) => param.startsWith('interactionCount='));
};

export const useCookieConsent = () => {
  const consentCookie = useCookie(ONETRUST_CONSENT_COOKIE_NAME);
  const intervalId = ref<NodeJS.Timeout | null>(null);
  const consentCookieState = ref(consentCookie.value);
  const hasInteractedWithCookieConsentBox: Ref<boolean> = ref(false);

  const updateConsentCookieStateIfDifferent = () => {
    const newCookieValue = useCookie(ONETRUST_CONSENT_COOKIE_NAME).value;
    if (newCookieValue !== consentCookieState.value) {
      consentCookieState.value = newCookieValue;

      if (consentCookieState.value) {
        const interactionCount = getInteractionCountParamFromCookieValue(
          consentCookieState.value,
        );
        const parsedInteractionCount = parseInteractionCountParamToNumber(
          interactionCount || '',
        );
        hasInteractedWithCookieConsentBox.value = parsedInteractionCount > 0;
      }
    }
  };

  const categoryAcceptanceMap = computed(() => {
    const newCategoryAcceptanceMap: Record<string, boolean> = {
      [NECESSARY]: true,
      [FUNCTIONAL]: false,
      [STATISTICS]: false,
      [MARKETING]: false,
    };

    if (!consentCookieState.value) {
      return newCategoryAcceptanceMap;
    }

    const groupsParamString = getGroupsParamFromCookieValue(
      consentCookieState.value,
    );
    if (!groupsParamString) {
      return newCategoryAcceptanceMap;
    }

    const commaSeparatedCategoryAcceptanceList =
      parseGroupsParamToCategoryAcceptanceList(groupsParamString);

    commaSeparatedCategoryAcceptanceList.forEach(
      (commaSeparatedCategoryAcceptance: string) => {
        const [categoryCode, acceptanceValue] = splitCategoryCodeAndAcceptance(
          commaSeparatedCategoryAcceptance,
        );

        const categoryName = oneTrustCategoryCodeToCategoryName[categoryCode];
        if (categoryName) {
          newCategoryAcceptanceMap[categoryName] = acceptanceValue === '1';
        }
      },
    );

    return newCategoryAcceptanceMap;
  });

  if (import.meta.client && !intervalId.value) {
    intervalId.value = setInterval(
      updateConsentCookieStateIfDifferent,
      COOKIE_CHECK_INTERVAL_MS,
    );
  }

  onBeforeUnmount(() => {
    if (intervalId.value) {
      clearInterval(intervalId.value);
    }
  });

  // prettier-ignore
  return {
    necessaryConsented: computed(() => categoryAcceptanceMap.value[NECESSARY] || false),
    functionalConsented: computed(() => categoryAcceptanceMap.value[FUNCTIONAL] || false),
    statisticsConsented: computed(() => categoryAcceptanceMap.value[STATISTICS] || false),
    marketingConsented: computed(() => categoryAcceptanceMap.value[MARKETING] || false),
    hasInteractedWithCookieConsentBox
  };
};
